import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { RequestError } from '@/services/config';
import { StockingCyclesController_updateAtSite } from '@/services/services';

import { sendWireBody } from '../shared/wireRequest';

import { invalidateSiteBoardQueries } from './_invalidateSiteBoard';
import { mapUpdateStockingCycleToRequest } from './mappers';
import type { UpdateStockingCycleInput } from './types';

export interface UseUpdateStockingCycleResult {
    /** Resolves once the board has refetched; rejects on a request error. */
    update: (stockingCycleId: number, input: UpdateStockingCycleInput) => Promise<void>;
    isLoading: boolean;
    error: Error | RequestError | null;
    reset: () => void;
}

/** Edits a stocking of the site from its banner: name, planned week and notes. */
export const useUpdateStockingCycle = (
    clientId: number,
    locationId: number
): UseUpdateStockingCycleResult => {
    const queryClient = useQueryClient();
    const mutation = useMutation({
        mutationFn: async (variables: {
            stockingCycleId: number;
            input: UpdateStockingCycleInput;
        }) => {
            await sendWireBody(
                'patchRequest',
                StockingCyclesController_updateAtSite,
                { clientId, locationId, stockingCycleId: variables.stockingCycleId },
                mapUpdateStockingCycleToRequest(variables.input)
            );
            // The banner, the selector and every dialog read the cycle list.
            await invalidateSiteBoardQueries(queryClient, clientId, locationId);
        },
    });

    return {
        update: async (stockingCycleId, input) => {
            await mutation.mutateAsync({ stockingCycleId, input });
        },
        isLoading: mutation.isPending,
        error: mutation.error,
        reset: mutation.reset,
    };
};
